import { useMemo } from "react";

import { cn } from "@/lib/utils";

import { categories, categoryColors } from "../data/agents";
import { Agent, AgentCategory } from "../types";
import { AgentCard } from "./AgentCard";

interface AgentGridProps {
  agents: Agent[];
  onTryNow: (agentId: string) => void;
  isOnboarded?: boolean;
}

export const AgentGrid = ({
  agents,
  onTryNow,
  isOnboarded = false,
}: AgentGridProps) => {
  const agentsByCategory = useMemo(() => {
    const grouped: Record<AgentCategory, Agent[]> = {} as Record<
      AgentCategory,
      Agent[]
    >;

    // Initialize all categories
    categories.forEach((cat) => {
      grouped[cat] = [];
    });

    // Group agents by category
    agents.forEach((agent) => {
      if (grouped[agent.category]) {
        grouped[agent.category].push(agent);
      }
    });

    return grouped;
  }, [agents]);

  // Get categories that have agents
  const activeCategories = categories.filter(
    (cat) => agentsByCategory[cat]?.length > 0,
  );

  // Determine if an agent should be disabled
  const isAgentDisabled = (agent: Agent): boolean => {
    if (isOnboarded) {
      // Onboarded users: all agents are enabled
      return false;
    }
    // Non-onboarded users: only onboarding agent is enabled
    return !agent.isOnboarding;
  };

  return (
    <div className="flex flex-col gap-10">
      {activeCategories.map((category) => (
        <div key={category}>
          <span
            className={cn(
              "inline-block px-3 py-1 text-sm font-medium rounded-full mb-5",
              categoryColors[category],
            )}
          >
            {category}
          </span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {agentsByCategory[category].map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onTryNow={onTryNow}
                disabled={isAgentDisabled(agent)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
